/**
 * useLocationSummaryExport.ts
 * Builds and downloads a full .xlsx export of the ASN Gap Location Summary
 * table. Mirrors the `useLoadDetailsExport` pattern — it fetches ALL pages
 * for the currently applied filters/sort/search (via
 * `fetchAsnLocationDetailsAllPages`), converts the rows into the
 * `AsnGroupedRow` shape, then hands them to `buildAsnOnePagerWorkbook`.
 *
 * Required flow:
 *   Click Export → fetch page 0 → read totalPages → fetch remaining pages →
 *   combine all rows → build workbook → download.
 */

import { useCallback, useState } from 'react';
import { ASNLocationDetailsRequest, fetchAsnLocationDetailsAllPages } from '../api/asnOverviewKpiApi';
import { buildAsnGroupedRowFromApi, buildAsnGrandTotalMetricValues, ASN_METRIC_COLUMNS, AsnMetricColumn } from '../constants/asnOnePagerConstants';
import { buildAsnOnePagerWorkbook } from '../utils/asnOnePagerWorkbookBuilder';
import { downloadWorkbook, WorkbookDownloadError } from '../../../../AnalyticsHub/utils/workbookDownload';
import { useToast } from '../../../../../components/LhToastContainer';
import { AsnLocationGrainKey } from '../api/asnOverviewKpiApi';
import { GrainKey } from '../asnGapLocationFacts';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** e.g. "ASN_Location_Summary_Shipping_2026-08-27_143205.xlsx" */
export const buildLocationSummaryExportFileName = (perspective: string, now: Date): string => {
  const yyyy = now.getFullYear();
  const mm = pad2(now.getMonth() + 1);
  const dd = pad2(now.getDate());
  const hh = pad2(now.getHours());
  const min = pad2(now.getMinutes());
  const ss = pad2(now.getSeconds());
  const perspToken = perspective === 'receiving' ? 'Receiving' : 'Shipping';
  return `ASN_Location_Summary_${perspToken}_${yyyy}-${mm}-${dd}_${hh}${min}${ss}.xlsx`;
};

export interface UseLocationSummaryExportReturn {
  readonly isExporting: boolean;
  /**
   * Kicks off the export using `appliedRequest` (the currently applied
   * filters/sort/search from `state.asnLocationSummary.lastRequest`) plus
   * all grain keys and all metric columns — column-picker visibility only
   * affects the on-screen table, never the exported document.
   */
  readonly exportLocationSummary: (
    appliedRequest: ASNLocationDetailsRequest,
    visibleGrainKeys: AsnLocationGrainKey[],
    visibleMetricColumns?: AsnMetricColumn[],
  ) => Promise<void>;
}

export const useLocationSummaryExport = (): UseLocationSummaryExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const exportLocationSummary = useCallback(
    async (
      appliedRequest: ASNLocationDetailsRequest,
      visibleGrainKeys: AsnLocationGrainKey[],
      visibleMetricColumns: AsnMetricColumn[] = ASN_METRIC_COLUMNS,
    ) => {
      if (isExporting) return;
      setIsExporting(true);

      try {
        let combined;
        try {
          combined = await fetchAsnLocationDetailsAllPages(appliedRequest);
        } catch (error: any) {
          const msg =
            error?.response?.data?.message ||
            error?.message ||
            'Unable to export the Location Summary table. Please try again.';
          toast.show(msg, 'error', 'Export Failed');
          return;
        }

        if (!combined.rows || combined.rows.length === 0) {
          toast.show(
            'No data available for export.',
            'warning',
            'Export',
          );
          return;
        }

        const groupedRows = combined.rows.map(buildAsnGroupedRowFromApi);
        const grandTotalValues = combined.grandTotal
          ? buildAsnGrandTotalMetricValues(combined.grandTotal)
          : null;

        // Use all grain keys passed by the caller (always the full set from
        // the API response), falling back to the response's own effectiveGrainKeys.
        const grainKeys = (
          visibleGrainKeys.length ? visibleGrainKeys : combined.effectiveGrainKeys ?? []
        ) as unknown as GrainKey[];

        const perspToken = appliedRequest.perspective === 'receiving' ? 'Receiving' : 'Shipping';

        let workbook;
        try {
          workbook = buildAsnOnePagerWorkbook({
            sheetName: `ASN Gap ${perspToken}`,
            rows: groupedRows,
            grandTotalValues,
            columns: visibleMetricColumns,
            grainKeys,
          });
        } catch (error: any) {
          toast.show(
            error?.message || 'Failed to build the export file. Please try again.',
            'error',
            'Export Failed',
          );
          return;
        }

        const fileName = buildLocationSummaryExportFileName(appliedRequest.perspective, new Date());
        try {
          await downloadWorkbook(workbook, fileName);
        } catch (error: any) {
          const msg =
            error instanceof WorkbookDownloadError
              ? error.message
              : error?.message ||
                'The download was blocked by the browser. Please allow downloads and try again.';
          toast.show(msg, 'error', 'Export Failed');
          return;
        }

        toast.show(
          `Exported ${groupedRows.length} row${groupedRows.length === 1 ? '' : 's'} to ${fileName}`,
          'success',
          'Export Complete',
        );
      } finally {
        setIsExporting(false);
      }
    },
    [isExporting, toast],
  );

  return { isExporting, exportLocationSummary };
};

export default useLocationSummaryExport;
